import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { products } from '../mock/mockData';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';
import InternalLinks from '../components/seo/InternalLinks';

const Products = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Water Plant Machinery & Equipment | Filling Machines, RO Plants | Alden Technologies"
        description="Explore water filling machines (30-200 BPM), RO plants (2000-10000 LPH), PET bottle blowing, labelling and shrink packaging machines. Factory-direct prices with installation & warranty."
        path="/products"
      />
      <Breadcrumbs items={[{ label: 'Products' }]} />
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1648142375707-03918153f53e"
            alt="Our Products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-emerald-700/70"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our Products
          </h1>
          <p className="text-xl md:text-2xl text-emerald-100 max-w-3xl mx-auto">
            Complete turnkey beverage plant solutions for every business need
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {products.map((product) => (
              <Card key={product.id} className="border-2 border-gray-200 hover:border-emerald-500 hover:shadow-2xl transition-all duration-300 overflow-hidden group">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white">{product.title}</h3>
                    <p className="text-emerald-300 font-medium mt-1">{product.capacity}</p>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-700 mb-6 leading-relaxed">{product.shortDesc}</p>
                  <Button 
                    onClick={() => setSelectedProduct(product)}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    View Details
                    <ArrowRight className="ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Product Details Modal */}
          {selectedProduct && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedProduct(null)}>
              <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="relative h-80">
                  <img
                    src={selectedProduct.image}
                    alt={selectedProduct.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-8 left-8 right-8">
                    <h2 className="text-4xl font-bold text-white mb-2">{selectedProduct.title}</h2>
                    <p className="text-emerald-300 text-xl font-medium">Capacity: {selectedProduct.capacity}</p>
                  </div>
                  <button
                    onClick={() => setSelectedProduct(null)}
                    className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="p-8">
                  <p className="text-lg text-gray-700 mb-8 leading-relaxed">{selectedProduct.shortDesc}</p>

                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <CheckCircle className="w-6 h-6 text-emerald-600" />
                        Key Features
                      </h3>
                      <ul className="space-y-3">
                        {selectedProduct.features.map((feature) => (
                          <li key={feature} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Filter className="w-6 h-6 text-emerald-600" />
                        Applications
                      </h3>
                      <ul className="space-y-3">
                        {selectedProduct.applications.map((application) => (
                          <li key={application} className="flex items-start gap-3">
                            <ArrowRight className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{application}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Link to="/contact" className="flex-1">
                      <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg">
                        Request Quote
                      </Button>
                    </Link>
                    <Button 
                      onClick={() => setSelectedProduct(null)}
                      variant="outline"
                      className="flex-1 py-6 text-lg border-2"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Turnkey <span className="text-emerald-600">Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete project execution from concept to commissioning
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {[
              { id: "consultation", step: "01", title: "Consultation", desc: "Initial discussion and feasibility study" },
              { id: "design", step: "02", title: "Design", desc: "Customized plant design and layout" },
              { id: "manufacturing", step: "03", title: "Manufacturing", desc: "Equipment fabrication and testing" },
              { id: "installation", step: "04", title: "Installation", desc: "On-site installation and setup" },
              { id: "support", step: "05", title: "Support", desc: "Training and after-sales service" }
            ].map((item) => (
              <div key={item.id} className="text-center">
                <div className="w-20 h-20 bg-emerald-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Need a Custom Solution?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            We design and build customized beverage plants based on your specific requirements
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Get Custom Quote
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
      <InternalLinks currentPath="/products" />
    </div>
  );
};

export default Products;
